<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

export default {
    name: 'MapComponent',
    props: {
        places: {type: Array, required: true},
        types: {type: Array, required: true},
        primarycolor: {type: String, default: "#3498db"},
        secondarycolor: {type: String, default: "#6c757d"},
        primarytextcolor: {type: String, default: "#ffffff"},
        secondarytextcolor: {type: String, default: "#ffffff"}
    },
    data() {
        return {
            map: null,
            markersLayer: null,
            selectedType: null,
            selectedTags: [],
            availableTags: [],
            primaryMarkerIcon: null,
            viewMode: 'map',
            hideTypeSelector: false
        }
    },
    computed: {
        filteredPlaces() {
            let result = this.places.filter(p => p.type === this.selectedType)

            if (this.selectedTags.length > 0) {
                result = result.filter(place =>
                    place.tags.some(tag => this.selectedTags.includes(tag.id))
                )
            }

            return result.sort((a, b) => {
                const titleA = this.getDisplayTitle(a).toLowerCase()
                const titleB = this.getDisplayTitle(b).toLowerCase()
                return titleA.localeCompare(titleB)
            })
        }
    },

    mounted() {
        console.log('Places', this.places)

        const urlParams = new URLSearchParams(window.location.search)
        const typParam = urlParams.get('typ')

        if (typParam && this.types.some(t => t.id === Number(typParam))) {
            this.selectedType = Number(typParam)
            this.hideTypeSelector = true
        } else {
            this.selectedType = this.types[0]?.id || null
            this.hideTypeSelector = false
        }

        this.updateAvailableTags()
        this.initMap()
        this.updateMarkers()
    },
    methods: {
        initMap() {
            this.primaryMarkerIcon = L.icon({
                iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
            <path fill="${this.primarycolor}" d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41C12.5 41 25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z"/>
            <circle fill="white" cx="12.5" cy="12" r="4.5"/>
          </svg>
        `)}`,
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowUrl: '/images/leaflet/marker-shadow.png',
                shadowSize: [41, 41],
                shadowAnchor: [12, 41]
            })

            this.map = L.map('map')
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: ''
            }).addTo(this.map)


            this.markersLayer = L.layerGroup().addTo(this.map)
        },
        getDisplayTitleTooltip(place) {
            const cityDistrict = this.getCityDistrict(place)
            const address = place.address_1 || ''
            const title = place.title || ''
            if (place.type === 1) {


                if (address && address.trim() !== '') {
                        return `<p style="margin-bottom: 3px;"><b>${cityDistrict}, ${address}</b><br></p>`.trim()
                }


                return `${cityDistrict} ${address}`.trim()
            }
            if (address && address.trim() !== '') {
                return `<p style="margin-bottom: 3px;"><b>${title}</b><br> ${cityDistrict}, ${address}<br></p>`.trim()
            }
            return `<p style="margin-bottom: 3px;"><b>${title}</b><br> ${cityDistrict}<br></p>`.trim()

        },
        getDisplayTitle(place) {
            const cityDistrict = this.getCityDistrict(place)
            const address = place.address_1 || ''
            const title = place.title || ''
            if (place.type === 1) {
                if (address && address.trim() !== '') {
                    return `${cityDistrict}, ${address}`.trim()
                }
                return cityDistrict.trim()
            }
            if (address && address.trim() !== '') {
                return `${title} (${cityDistrict}, ${address})`.trim()
            }
            return `${title} (${cityDistrict})`.trim()
        },
        getCityDistrict(place) {
            const city = place.city || ''
            const district = place.district || ''
            return district && city !== district ? `${city} OT ${district}` : city
        },
        updateAvailableTags() {
            const filteredPlaces = this.places.filter(p => p.type === this.selectedType)
            const allTags = filteredPlaces.flatMap(place => place.tags)
            const uniqueTagsMap = new Map()
            allTags.forEach(tag => {
                if (!uniqueTagsMap.has(tag.id)) uniqueTagsMap.set(tag.id, tag)
            })
            this.availableTags = Array.from(uniqueTagsMap.values())
        },
        updateMarkers() {
            if (!this.map) return
            this.markersLayer.clearLayers()

            const filtered = this.filteredPlaces
            filtered.forEach(place => {
                const marker = L.marker(
                    [parseFloat(place.latitude), parseFloat(place.longitude)],
                    {icon: this.primaryMarkerIcon}
                )

                const tagsHtml = place.tags.map(tag => {
                    const tagName = (tag.name?.de) || tag.name || tag.slug
                    return `<li>${tagName}</li>`
                }).join('')

                marker.bindPopup(`
    ${this.getDisplayTitleTooltip(place)}
    Hier befindet sich:<br>
    <ul style="margin:0; padding-left:20px;">${tagsHtml}</ul>
`)


                this.markersLayer.addLayer(marker)
            })

            if (filtered.length > 0) {
                const bounds = L.latLngBounds(filtered.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]))
                this.map.fitBounds(bounds)
            }
        },
        onTypeChange(event) {
            this.selectedType = parseInt(event.target.value)
            this.selectedTags = []
            this.updateAvailableTags()
            this.updateMarkers()
        },
        onTagToggle(tagId) {
            if (this.selectedTags.includes(tagId)) {
                this.selectedTags = this.selectedTags.filter(id => id !== tagId)
            } else {
                this.selectedTags.push(tagId)
            }
            this.updateMarkers()
        },
        resetTags() {
            this.selectedTags = []
            this.updateMarkers()
        },
        toggleView(mode) {
            this.viewMode = mode
            if (mode === 'map' && this.map) {
                this.$nextTick(() => {
                    this.map.invalidateSize()
                    setTimeout(() => {
                        this.map.invalidateSize()
                    }, 200)
                    this.updateMarkers()
                })
            }
        }
    }
}
</script>

<template>
    <div class="map-container" :style="{'--primary-color': primarycolor, '--secondary-color': secondarycolor, '--primary-text-color': primarytextcolor, '--secondary-text-color': secondarytextcolor}">
        <!-- Filter -->
        <select v-if="!hideTypeSelector" @change="onTypeChange" :value="selectedType">
            <option v-for="type in types" :key="type.id" :value="type.id">{{ type.value }}</option>
        </select>

        <div>

            <div class="tags">
                <label v-for="(tag, index) in availableTags" :key="tag.id"
                       :class="[{ 'first-child': index === 0, 'last-child': index === availableTags.length -1 }]">
                    <input type="checkbox" :value="tag.id" @change="onTagToggle(tag.id)"
                           :checked="selectedTags.includes(tag.id)">
                    {{ (tag.name && tag.name.de) ? tag.name.de : (tag.name || tag.slug) }}
                </label>
            </div>

            <button class="reset-button"
                    :class="{'active': selectedTags.length === 0} "
                    @click="resetTags">Alle anzeigen</button>
        </div>
        <!-- View Toggle -->
        <div class="view-toggle">
            <button @click="toggleView('map')" :class="{active: viewMode === 'map'}">Karte</button>
            <button @click="toggleView('list')" :class="{active: viewMode === 'list'}">Liste</button>
        </div>

        <!-- Map View -->
        <div v-show="viewMode === 'map'" class="map-wrapper">
            <div id="map"></div>
        </div>

        <!-- List View -->
        <div v-show="viewMode === 'list'" class="list-wrapper">
            <div v-for="place in filteredPlaces" :key="place.id" class="list-item">
                <h4>{{ getDisplayTitle(place) }}</h4>
                <div class="tags-inline">
          <span v-for="tag in place.tags" :key="tag.id" class="tag">
            {{ (tag.name && tag.name.de) ? tag.name.de : (tag.name || tag.slug) }}
          </span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.map-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;

    select {
        padding: 8px 26px 8px 16px;
        background-color: var(--primary-color);
        color: var(--primary-text-color);
        border: none;
        border-radius: 4px;
        appearance: none;
        background-image: url("data:image/svg+xml;utf8,<svg fill='white' height='16' viewBox='0 0 24 24' width='16' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
        background-repeat: no-repeat;
        background-position: right 8px center;
        cursor: pointer;
        font-weight: bold;
        width: fit-content;
        transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
        outline: none;
    }

    .tags {
        display: flex;
        flex-wrap: wrap;


        label {
            padding: 6px 12px;
            background-color: var(--primary-color);
            border-radius: 0;
            cursor: pointer;
            font-weight: bold;
            color: var(--primary-text-color);
            transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

            input {
                appearance: none;
            }

            &.first-child {
                border-top-left-radius: 24px;
                border-bottom-left-radius: 24px;
            }

            &.last-child {
                border-top-right-radius: 24px;
                border-bottom-right-radius: 24px;
            }

            &:hover,
            &:has(input:checked) {
                background-color: var(--secondary-color);
                color: var(--secondary-text-color);
            }
        }
    }

    .reset-button {
        padding: 6px 12px;
        background-color: var(--primary-color);
        border-radius: 24px;
        cursor: pointer;
        border: none;
        font-weight: bold;
        width: fit-content;
        color: var(--primary-text-color);
        transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

        &:hover, &.active {
            background-color: var(--secondary-color);
            color: var(--secondary-text-color);
        }
    }

    .view-toggle {
        display: flex;
        justify-content: flex-end;
        gap: 8px;

        button {
            padding: 6px 12px;
            border-radius: 24px;
            border: none;
            background-color: var(--primary-color);
            font-weight: bold;
            cursor: pointer;
            width: fit-content;
            color: var(--primary-text-color);
            transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

            &.active,
            &:hover {
                background-color: var(--secondary-color);
                color: var(--secondary-text-color);
            }
        }
    }

    .map-wrapper {
        #map {
            height: 700px;
            width: 100%;
        }
    }

    .list-wrapper {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .list-item {
            padding: 12px;
            background-color: #f9f9f9;
            border-radius: 12px;

            box-shadow: 3px 3px 15px 0 rgba(0,0,0,0.2);
            -webkit-box-shadow: 3px 3px 15px 0 rgba(0,0,0,0.2);

            h4 {
                margin-bottom: 6px;
                font-weight: bold;
            }

            .tags-inline {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;

                .tag {
                    padding: 4px 8px;
                    border-radius: 12px;
                    background-color: var(--primary-color);
                    font-size: 0.85em;
                    color: var(--primary-text-color);
                    transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
                }
            }
        }
    }
}

.leaflet-control-attribution {
    display: none !important;
}

</style>
